import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { usePaymentInputs } from 'react-payment-inputs';
import images from 'react-payment-inputs/images';

import CommonIcons from '../../icons/Common';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

const ValidatedCardInput = ({ errors, handleInputChange, handleInputBlur }) => {
	const { t } = useTranslation('paymentInfo');
	const [showCardNumber, setShowCardNumber] = useState(true);
	const [cardNumber, setCardNumber] = useState('');

	const {
		meta,
		getCardNumberProps,
		getExpiryDateProps,
		getCVCProps,
		getCardImageProps
	} = usePaymentInputs();

	useEffect(() => {
		handleInputChange('typeOfCard', meta.cardType?.displayName || '');
	}, [meta.cardType?.displayName]);

	const handleCardNumberChange = (e) => {
		setCardNumber(e.target.value);
		handleInputChange('cardNumber', e.target.value);
	};

	const cardNumberProps = getCardNumberProps({
		onChange: handleCardNumberChange,
		onBlur: (e) => handleInputBlur('cardNumber', e.target.value)
	});

	const expiryDateProps = getExpiryDateProps({
		onChange: (e) => handleInputChange('expiryDate', e.target.value),
		onBlur: (e) => handleInputBlur('expiryDate', e.target.value)
	});

	const cvvProps = getCVCProps({
		onChange: (e) => handleInputChange('cvc', e.target.value),
		onBlur: (e) => handleInputBlur('cvc', e.target.value)
	});

	const handleToggleVisibility = () => {
		setShowCardNumber(!showCardNumber);
	};

	const handleMouseDown = (e) => {
		e.preventDefault();
	};

	return (
		<Stack spacing={2}>
			<Box>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						border: '1px solid',
						borderColor: errors.cardNumber
							? 'error.main'
							: 'rgba(0, 0, 0, 0.23)',
						borderRadius: 1,
						'&:hover': {
							borderColor: errors.cardNumber
								? 'error.main'
								: 'rgba(0, 0, 0, 0.87)'
						},
						'&:focus-within': {
							borderColor: 'primary.main',
							borderWidth: 2
						}
					}}
				>
					<InputAdornment position='start' sx={{ ml: 1.5 }}>
						<Box
							component='svg'
							{...getCardImageProps({ images })}
							sx={{ width: 28, height: 28 }}
						/>
					</InputAdornment>
					<Box sx={{ flex: 1 }}>
						<input
							{...cardNumberProps}
							name='cardNumber'
							placeholder={`${t('cardNumber')} *`}
							style={{
								border: 'none',
								outline: 'none',
								padding: '16.5px 14px',
								fontSize: '16px',
								width: '100%',
								backgroundColor: 'transparent',
								fontFamily: 'inherit',
								WebkitTextSecurity: showCardNumber ? 'none' : 'disc'
							}}
						/>
					</Box>
					<IconButton
						aria-label={
							showCardNumber ? t('hideCardNumber') : t('showCardNumber')
						}
						onClick={handleToggleVisibility}
						onMouseDown={handleMouseDown}
						edge='end'
						size='small'
						sx={{ mr: 1 }}
					>
						{showCardNumber ? <CommonIcons.show /> : <CommonIcons.hide />}
					</IconButton>
				</Box>
				{errors.cardNumber && (
					<Typography
						variant='caption'
						color='error'
						sx={{ mt: 0.5, ml: 1.75, display: 'block' }}
					>
						{errors.cardNumber}
					</Typography>
				)}
			</Box>

			<Stack direction='row' spacing={2}>
				<Box sx={{ flex: 1 }}>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							border: '1px solid',
							borderColor: errors.expiryDate
								? 'error.main'
								: 'rgba(0, 0, 0, 0.23)',
							borderRadius: 1,
							'&:hover': {
								borderColor: errors.expiryDate
									? 'error.main'
									: 'rgba(0, 0, 0, 0.87)'
							},
							'&:focus-within': {
								borderColor: 'primary.main',
								borderWidth: 2
							}
						}}
					>
						<input
							{...expiryDateProps}
							name='expiryDate'
							placeholder={`${t('expiryDate')} *`}
							style={{
								border: 'none',
								outline: 'none',
								padding: '16.5px 14px',
								fontSize: '16px',
								width: '100%',
								backgroundColor: 'transparent',
								fontFamily: 'inherit'
							}}
						/>
					</Box>
					{errors.expiryDate && (
						<Typography
							variant='caption'
							color='error'
							sx={{ mt: 0.5, ml: 1.75, display: 'block' }}
						>
							{errors.expiryDate}
						</Typography>
					)}
				</Box>

				<Box sx={{ flex: 1 }}>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							border: '1px solid',
							borderColor: errors.cvc ? 'error.main' : 'rgba(0, 0, 0, 0.23)',
							borderRadius: 1,
							'&:hover': {
								borderColor: errors.cvc ? 'error.main' : 'rgba(0, 0, 0, 0.87)'
							},
							'&:focus-within': {
								borderColor: 'primary.main',
								borderWidth: 2
							}
						}}
					>
						<input
							{...cvvProps}
							name='cvc'
							placeholder={`${t('cvv')} *`}
							style={{
								border: 'none',
								outline: 'none',
								padding: '16.5px 14px',
								fontSize: '16px',
								width: '100%',
								backgroundColor: 'transparent',
								fontFamily: 'inherit'
							}}
						/>
					</Box>
					{errors.cvc && (
						<Typography
							variant='caption'
							color='error'
							sx={{ mt: 0.5, ml: 1.75, display: 'block' }}
						>
							{errors.cvc}
						</Typography>
					)}
				</Box>
			</Stack>
		</Stack>
	);
};

ValidatedCardInput.propTypes = {
	errors: PropTypes.object.isRequired,
	handleInputChange: PropTypes.func.isRequired,
	handleInputBlur: PropTypes.func.isRequired
};

export default ValidatedCardInput;
